import apiService from '../services/apiService';
import gandiService from '../services/gandiService';
import {
  getDomainStatus,
  getDaysUntilExpiration,
  getRenewalState,
} from '../utils/domainUtils';
import {
  getGandiExpirationDate,
  getGandiDomainStatus,
  getGandiDaysUntilExpiration,
  getGandiRenewalState,
} from '../utils/gandiDomainUtils';

const GANDI_FALLBACK_DOMAINS = [
  {
    id: 1,
    fqdn: 'exemple.com',
    organization: 'org4',
    status: ['clientTransferProhibited'],
    autoRenew: true,
    dateCreated: '2020-03-16T09:40:08Z',
    dateRegistryEnd: '2026-03-16T08:40:08Z',
    dateUpdated: '2025-02-13T07:55:26Z',
    nameservers: ['a.dns.gandi.net', 'b.dns.gandi.net', 'c.dns.gandi.net'],
    contacts: {},
    dnsKeys: [],
    glueRecords: [],
    livednsInfo: {},
    restoreInfo: null,
    tags: [],
    webRedirections: [],
    rawData: {
      basic: {
        fqdn: 'exemple.com',
        tld: 'com',
        status: ['clientTransferProhibited'],
        dates: {
          created_at: '2020-03-16T09:40:08Z',
          registry_ends_at: '2026-03-16T08:40:08Z',
          updated_at: '2025-02-13T07:55:26Z',
        },
        autorenew: { enabled: true },
      },
    },
    lastSync: '2025-10-15 09:40:55',
    error: null,
  },
  {
    id: 2,
    fqdn: 'test.fr',
    organization: 'org5',
    status: [],
    autoRenew: false,
    dateCreated: '2019-01-24T17:08:35Z',
    dateRegistryEnd: '2025-12-24T16:08:34Z',
    dateUpdated: '2024-11-25T16:48:54Z',
    nameservers: ['a.dns.gandi.net', 'b.dns.gandi.net', 'c.dns.gandi.net'],
    contacts: {},
    dnsKeys: [],
    glueRecords: [],
    livednsInfo: {},
    restoreInfo: null,
    tags: [],
    webRedirections: [],
    rawData: {
      basic: {
        fqdn: 'exemple.com',
        tld: 'com',
        status: ['clientTransferProhibited'],
        dates: {
          created_at: '2020-03-16T09:40:08Z',
          registry_ends_at: '2026-03-16T08:40:08Z',
          updated_at: '2025-02-13T07:55:26Z',
        },
        autorenew: { enabled: true },
      },
    },
    lastSync: '2025-10-15 09:25:14',
    error: null,
  },
  {
    id: 3,
    fqdn: 'expire-bientot.org',
    organization: 'org6',
    status: ['clientTransferProhibited'],
    autoRenew: true,
    dateCreated: '2020-01-15T10:30:00Z',
    dateRegistryEnd: '2025-01-20T10:30:00Z',
    dateUpdated: '2024-12-01T14:20:00Z',
    nameservers: ['a.dns.gandi.net', 'b.dns.gandi.net', 'c.dns.gandi.net'],
    contacts: {},
    dnsKeys: [],
    glueRecords: [],
    livednsInfo: {},
    restoreInfo: null,
    tags: [],
    webRedirections: [],
    rawData: {
      basic: {
        fqdn: 'exemple.com',
        tld: 'com',
        status: ['clientTransferProhibited'],
        dates: {
          created_at: '2020-03-16T09:40:08Z',
          registry_ends_at: '2026-03-16T08:40:08Z',
          updated_at: '2025-02-13T07:55:26Z',
        },
        autorenew: { enabled: true },
      },
    },
    lastSync: '2025-01-10 08:15:30',
    error: null,
  },
];

const OVH_FALLBACK_DOMAINS = [
  {
    id: 1,
    name: 'example.com',
    status: 'ok',
    expirationDate: '2025-12-31T00:00:00Z',
    autoRenew: true,
    details: {
      offer: 'gold',
      dnssecState: 'enabled',
      renewalState: 'automatic_renew',
      renewalType: 'automaticV2016',
    },
    contacts: null,
    dns: null,
    renewal: {
      renew: {
        automatic: true,
        manualPayment: false,
      },
    },
    error: null,
  },
  {
    id: 2,
    name: 'test.fr',
    status: 'ok',
    expirationDate: '2025-11-15T00:00:00Z',
    autoRenew: false,
    details: {
      offer: 'silver',
      dnssecState: 'disabled',
      renewalState: 'manual_renew',
      renewalType: 'manualV2016',
    },
    contacts: null,
    dns: null,
    renewal: {
      renew: {
        automatic: false,
        manualPayment: true,
      },
    },
    error: null,
  },
  {
    id: 3,
    name: 'expired-domain.com',
    status: 'ok',
    expirationDate: '2024-10-01T00:00:00Z',
    autoRenew: false,
    details: {
      offer: 'gold',
      dnssecState: 'enabled',
      renewalState: 'no_renew',
      renewalType: 'noRenew',
    },
    contacts: null,
    dns: null,
    renewal: {
      renew: {
        automatic: false,
        manualPayment: false,
        deleteAtExpiration: true,
      },
    },
    error: null,
  },
];

export const gandiDomainListAdapter = {
  title: 'Domaines Gandi',
  subtitle: 'Domaines enregistrés chez Gandi, avec leur date d’expiration et leur renouvellement.',
  syncLabel: 'Synchroniser Gandi',
  filterPrefix: 'gandi',
  nameSortKey: 'fqdn',
  timingLabel: 'Gandi Domain Loading',
  detailPath: (id) => `/iti/domaines/gandi/${id}`,
  getMeta: (domainInfo) => domainInfo.organization || '',
  isConfigured: () => gandiService.isConfigured(),
  listDomains: async () => {
    const domainsData = await gandiService.getDomains();
    return domainsData.map((domain) => ({ ...domain, error: null }));
  },
  fetchDomainDetails: (domainId) => gandiService.getDomainDetails(domainId),
  syncDomains: () => gandiService.syncDomains(),
  linkClient: (id, thirdpartyId) => apiService.patch(`/gandi/domains/${id}/client`, { dolibarrThirdpartyId: thirdpartyId }),
  autoLinkClients: () => apiService.post('/gandi/domains/link-auto'),
  testConnection: () => gandiService.testConnection(),
  getFallbackDomains: () => GANDI_FALLBACK_DOMAINS.map((d) => ({ ...d })),
  getName: (domainInfo) => domainInfo.fqdn,
  getExpiration: getGandiExpirationDate,
  getStatus: getGandiDomainStatus,
  getRenewal: getGandiRenewalState,
  getDays: getGandiDaysUntilExpiration,
};

export const ovhDomainListAdapter = {
  title: 'Domaines OVH',
  subtitle: 'Domaines enregistrés chez OVH, avec leur date d’expiration et leur renouvellement.',
  syncLabel: 'Synchroniser OVH',
  filterPrefix: 'ovh',
  nameSortKey: 'domain',
  timingLabel: 'Domain Loading',
  detailPath: (id) => `/iti/domaines/ovh/${id}`,
  getMeta: () => '',
  isConfigured: () => apiService.isConfigured(),
  listDomains: async () => {
    const result = await apiService.getDomainsWithPagination({ limit: 10000 });
    return result.domains.map((domain) => ({ ...domain, error: null }));
  },
  fetchDomainDetails: (domainId) => apiService.getDomainById(domainId),
  syncDomains: () => apiService.syncDomains(),
  linkClient: (id, thirdpartyId) => apiService.patch(`/domains/${id}/client`, { dolibarrThirdpartyId: thirdpartyId }),
  autoLinkClients: () => apiService.post('/domains/ovh/link-auto'),
  testConnection: () => apiService.testConnection(),
  getFallbackDomains: () => OVH_FALLBACK_DOMAINS.map((d) => ({ ...d })),
  getName: (domainInfo) => domainInfo.name,
  getExpiration: (domainInfo) => domainInfo.expirationDate,
  getStatus: getDomainStatus,
  getRenewal: getRenewalState,
  getDays: (domainInfo) => getDaysUntilExpiration(domainInfo.expirationDate),
};
